<script setup lang="ts">
  import { computed, nextTick, ref, watch } from "vue";
  import { CircleAlert, Copy, Check, Paperclip, ThumbsDown, ThumbsUp } from "lucide-vue-next";
  import { Button } from "@/components/ui/button";
  import { renderMarkdown } from "@utils/renderMarkdown";
  import { copyMarkdown } from "@utils/markdownClipboard";
  import AiAssistantInput from "@/components/episto-ui/ai-assistant/AiAssistantInput.vue";
  import AssistantAskForm from "./AssistantAskForm.vue";
  import ProgrammingCheckReport from "@/components/Survey/ProgrammingCheck/ProgrammingCheckReport.vue";
  import AssistantEmptyState from "./AssistantEmptyState.vue";
  import AssistantSteps from "./AssistantSteps.vue";
  import type { RenderedStep } from "@utils/assistantActivity";
  import { useSurveyCardsStore } from "@stores/survey/surveyCards";
  import type { AssistantAnswer, AssistantQuestionForm } from "@stores/assistant";

  interface ThreadMessage {
    id: string;
    role: "user" | "assistant" | "error";
    content: string;
    steps?: RenderedStep[];
    stepsSummary?: string;
    feedback?: string | null;
    files?: { name: string; url?: string }[];
    question?: AssistantQuestionForm | null;
    programmingCheck?: boolean;
  }

  interface ScopeHint {
    verb: string;
    target: string;
    filters: number;
  }

  const props = withDefaults(
    defineProps<{
      messages: ThreadMessage[];
      isStreaming?: boolean;
      streamingContent?: string;
      streamingSteps?: RenderedStep[];
      streamingSummary?: string;
      suggestionGroups?: Record<string, string[]>;
      scopeHint?: ScopeHint | null;
      firstName?: string;
      canRetry?: boolean;
      surveyToken?: string | null;
    }>(),
    {
      isStreaming: false,
      streamingContent: "",
      streamingSteps: () => [],
      streamingSummary: "",
      suggestionGroups: () => ({}),
      scopeHint: null,
      firstName: "",
      canRetry: false,
      surveyToken: null,
    },
  );

  const emit = defineEmits<{
    send: [content: string, files: File[]];
    stop: [];
    retry: [];
    feedback: [messageId: string, value: "positive" | "negative"];
    answer: [messageId: string, text: string, answers: Record<string, AssistantAnswer>];
  }>();

  const { selectCurrentCard } = useSurveyCardsStore();
  const container = ref<HTMLElement | null>(null);
  const composer = ref<InstanceType<typeof AiAssistantInput> | null>(null);
  const expanded = ref<Set<string>>(new Set());
  const copiedId = ref<string | null>(null);

  const THINKING: RenderedStep[] = [{ label: "Thinking…", detail: "", duration: "", live: true }];

  const proseClasses =
    "ai-prose prose prose-sm max-w-none text-foreground/90 prose-p:my-1 prose-p:leading-relaxed prose-headings:font-semibold prose-headings:leading-snug prose-h1:text-[1.05em] prose-h2:text-[1em] prose-h3:text-[0.95em] prose-headings:my-2 prose-ul:my-1.5 prose-ol:my-1.5 prose-li:my-0.5 prose-strong:font-medium prose-a:text-ai-text prose-a:no-underline hover:prose-a:underline prose-code:text-[0.85em] prose-code:bg-muted prose-code:px-1 prose-code:py-0.5 prose-code:rounded prose-code:before:content-none prose-code:after:content-none prose-pre:bg-muted prose-pre:text-[0.85em] prose-pre:my-1.5 prose-blockquote:border-border prose-blockquote:text-muted-foreground prose-hr:border-border prose-th:font-medium prose-th:text-muted-foreground";

  const hasThread = computed(() => props.messages.length > 0 || props.isStreaming);
  const programmingCheckMessageId = computed(() => [...props.messages].reverse().find((message) => message.programmingCheck)?.id);

  function collapsed(id: string) {
    return !expanded.value.has(id);
  }

  function setCollapsed(id: string, value: boolean) {
    const next = new Set(expanded.value);
    if (value) next.delete(id);
    else next.add(id);
    expanded.value = next;
  }

  async function copy(message: ThreadMessage) {
    await copyMarkdown(message.content);
    copiedId.value = message.id;
    setTimeout(() => {
      if (copiedId.value === message.id) copiedId.value = null;
    }, 1600);
  }

  defineExpose({ focusComposer: () => composer.value?.focus() });

  const NEAR_BOTTOM_PX = 4;
  const SCROLL_UP_KEYS = ["ArrowUp", "PageUp", "Home"];
  const followsBottom = ref(true);
  let ownScroll = false;

  function distanceToBottom(el: HTMLElement) {
    return el.scrollHeight - el.scrollTop - el.clientHeight;
  }

  function stopFollowing() {
    followsBottom.value = false;
  }

  function onWheel(event: WheelEvent) {
    if (event.deltaY < 0) stopFollowing();
  }

  function onKeydown(event: KeyboardEvent) {
    if (SCROLL_UP_KEYS.includes(event.key)) stopFollowing();
  }

  function onScroll() {
    const el = container.value;
    if (!el) return;
    if (ownScroll) {
      ownScroll = false;
      return;
    }
    followsBottom.value = distanceToBottom(el) <= NEAR_BOTTOM_PX;
  }

  function scrollToBottom() {
    nextTick(() => {
      const el = container.value;
      if (!el || distanceToBottom(el) <= 0) return;
      ownScroll = true;
      el.scrollTop = el.scrollHeight;
    });
  }

  watch(
    () => props.messages.length,
    (length, previous) => {
      const sentOrOpened = !previous || props.messages[length - 1]?.role === "user";
      if (sentOrOpened) followsBottom.value = true;
      if (followsBottom.value) scrollToBottom();
    },
  );

  watch(
    () => props.messages[0]?.id,
    () => {
      followsBottom.value = true;
      scrollToBottom();
    },
  );

  watch(
    () => [props.streamingContent, props.streamingSteps.length],
    () => {
      if (followsBottom.value) scrollToBottom();
    },
  );
</script>

<template>
  <div class="flex h-full min-h-0 flex-col overflow-hidden">
    <div
      ref="container"
      class="flex min-h-0 flex-1 flex-col gap-[18px] overflow-y-auto px-4 py-4"
      @scroll.passive="onScroll"
      @wheel.passive="onWheel"
      @touchmove.passive="stopFollowing"
      @keydown="onKeydown">
      <AssistantEmptyState
        v-if="!hasThread"
        :groups="suggestionGroups"
        :first-name="firstName"
        @pick="(prompt) => emit('send', prompt, [])" />

      <template
        v-for="message in messages"
        :key="message.id">
        <div
          v-if="message.role === 'user'"
          class="ai-rise flex justify-end">
          <div class="max-w-[85%] whitespace-pre-wrap rounded-2xl rounded-br-md bg-muted px-[13px] py-2 text-13 leading-normal">
            {{ message.content }}
            <div
              v-if="message.files?.length"
              class="mt-2 flex flex-wrap gap-1.5">
              <component
                :is="file.url ? 'a' : 'span'"
                v-for="file in message.files"
                :key="file.name"
                :href="file.url"
                :download="file.url ? file.name : undefined"
                :title="file.url ? `Download ${file.name}` : file.name"
                class="inline-flex items-center gap-1 rounded-md border border-border bg-background px-1.5 py-0.5 text-2xs text-muted-foreground"
                :class="file.url ? 'cursor-pointer hover:underline' : ''">
                <Paperclip class="h-2.5 w-2.5 shrink-0" />
                <span class="max-w-[160px] truncate">{{ file.name }}</span>
              </component>
            </div>
          </div>
        </div>

        <div
          v-else-if="message.role === 'error'"
          class="ai-rise flex items-start gap-2.5 rounded-lg border border-destructive/25 bg-destructive/5 px-3 py-2.5 text-xs">
          <CircleAlert class="mt-0.5 h-[15px] w-[15px] shrink-0 text-destructive" />
          <div class="flex-1">
            <b class="font-medium">The assistant could not finish</b>
            <p class="mt-0.5 text-muted-foreground">{{ message.content }}</p>
          </div>
          <Button
            v-if="canRetry"
            variant="outline"
            size="sm"
            class="h-[26px] shrink-0 px-2.5 text-xs"
            :disabled="isStreaming"
            @click="emit('retry')">
            Retry
          </Button>
        </div>

        <div
          v-else
          class="ai-rise group flex flex-col gap-2.5">
          <AssistantSteps
            v-if="message.steps?.length"
            :steps="message.steps"
            :summary="message.stepsSummary"
            :collapsed="collapsed(message.id)"
            @update:collapsed="(value) => setCollapsed(message.id, value)" />

          <div
            v-if="message.content"
            :class="proseClasses"
            v-html="renderMarkdown(message.content)" />

          <ProgrammingCheckReport
            v-if="surveyToken && message.id === programmingCheckMessageId"
            :key="message.id"
            :survey-token="surveyToken"
            :disabled="isStreaming"
            @card="(token) => selectCurrentCard({ token })"
            @rerun="emit('send', 'Check the programming of this survey again', [])"
            @fix="(prompt) => emit('send', prompt, [])" />

          <AssistantAskForm
            v-if="message.question?.questions?.length"
            :form="message.question"
            :disabled="isStreaming"
            @submit="(text, answers) => emit('answer', message.id, text, answers)" />

          <div
            v-if="message.content"
            class="-mt-1 flex items-center gap-0.5 opacity-0 transition-opacity group-hover:opacity-100"
            :class="message.feedback && message.feedback !== 'no_opinion' ? 'opacity-100' : ''">
            <Button
              variant="ghost"
              size="icon"
              class="h-6 w-6 text-muted-foreground"
              title="Copy"
              @click="copy(message)">
              <Check
                v-if="copiedId === message.id"
                class="h-[13px] w-[13px] text-ai-text" />
              <Copy
                v-else
                class="h-[13px] w-[13px]" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              class="h-6 w-6"
              :class="message.feedback === 'positive' ? 'bg-ai-soft text-ai-text' : 'text-muted-foreground'"
              title="Helpful answer"
              @click="emit('feedback', message.id, 'positive')">
              <ThumbsUp class="h-[13px] w-[13px]" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              class="h-6 w-6"
              :class="message.feedback === 'negative' ? 'bg-ai-soft text-ai-text' : 'text-muted-foreground'"
              title="Needs improvement"
              @click="emit('feedback', message.id, 'negative')">
              <ThumbsDown class="h-[13px] w-[13px]" />
            </Button>
          </div>
        </div>
      </template>

      <div
        v-if="isStreaming"
        class="flex flex-col gap-2.5">
        <AssistantSteps
          :steps="streamingSteps.length ? streamingSteps : THINKING"
          :summary="streamingSummary"
          :collapsed="false" />
        <div
          v-if="streamingContent"
          :class="proseClasses">
          <span v-html="renderMarkdown(streamingContent)" /><span
            class="ml-0.5 inline-block h-[14px] w-[3px] animate-pulse rounded-full bg-foreground/70 align-text-bottom" />
        </div>
      </div>
    </div>

    <AiAssistantInput
      ref="composer"
      :is-streaming="isStreaming"
      autofocus
      allow-files
      @send="(message: string, files: File[]) => emit('send', message, files)"
      @stop="emit('stop')" />

    <p
      v-if="scopeHint"
      class="shrink-0 px-4 pb-3 text-2xs text-muted-foreground">
      {{ scopeHint.verb }} <span class="font-medium text-foreground/80">{{ scopeHint.target }}</span>
      <template v-if="scopeHint.filters">
        · {{ scopeHint.filters }} active {{ scopeHint.filters === 1 ? "filter" : "filters" }}
      </template>
    </p>
  </div>
</template>
